import { useSearchParams } from 'react-router-dom';
import { SegmentedControl } from '@eo/shared';

import { AttributionTab } from './AttributionTab';
import { ClientsTab } from './ClientsTab';
import { HolidaysTab } from './HolidaysTab';
import { REGISTRY_VIEW_LABELS, REGISTRY_VIEWS } from './constants';
import { parseRegistryView } from './helpers';
import { StaffTab } from './StaffTab';
import { TaskTypesTab } from './TaskTypesTab';
import type { RegistriesTabProps, RegistryView } from './types';

const VIEW_OPTIONS = REGISTRY_VIEWS.map((value) => ({
  value,
  label: REGISTRY_VIEW_LABELS[value],
}));

export function RegistriesTab({
  staff,
  clients,
  options,
  onQueryOptions,
  taskTypes,
  pagination,
  staffPagination,
  taskTypesPagination,
  onSyncTaskTypes,
  onCreate,
  onUpdate,
  onCreateStaff,
  onUpdateStaff,
  holidays,
  year,
  onYearShift,
  onCreateHoliday,
  onRemoveHoliday,
  attribution,
  month,
}: RegistriesTabProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const view = parseRegistryView(searchParams.get('view'));

  const onViewChange = (next: RegistryView) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (next === 'staff') params.delete('view');
      else params.set('view', next);
      // Each registry grid answers list params in SQL, so a sort or page left
      // behind by the previous view would be rejected by the next route.
      for (const key of ['sort', 'search', 'page']) params.delete(key);
      return params;
    });
  };

  return (
    <section className="flex flex-col gap-4">
      <SegmentedControl<RegistryView>
        options={VIEW_OPTIONS}
        value={view}
        onChange={onViewChange}
        ariaLabel="Registry"
        className="w-fit"
      />

      {view === 'attribution' && <AttributionTab attribution={attribution} month={month} />}
      {view === 'holidays' && (
        <HolidaysTab
          holidays={holidays}
          year={year}
          onYearShift={onYearShift}
          onCreate={onCreateHoliday}
          onRemove={onRemoveHoliday}
        />
      )}
      {view === 'staff' && (
        <StaffTab
          staff={staff}
          clients={clients}
          options={options}
          onQueryOptions={onQueryOptions}
          pagination={staffPagination}
          onCreate={onCreateStaff}
          onUpdate={onUpdateStaff}
        />
      )}
      {view === 'clients' && (
        <ClientsTab
          clients={clients}
          staff={staff}
          options={options}
          onQueryOptions={onQueryOptions}
          pagination={pagination}
          onCreate={onCreate}
          onUpdate={onUpdate}
        />
      )}
      {view === 'task-types' && (
        <TaskTypesTab
          taskTypes={taskTypes}
          pagination={taskTypesPagination}
          onSync={onSyncTaskTypes}
        />
      )}
    </section>
  );
}
